import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { RouterProvider } from 'react-router'
import './i18n'
import './index.css'
import { router } from './ui/routes/router'
import { ThemeProvider } from './ui/theme/ThemeProvider'
import { applyFontSize, readFontSize } from './ui/theme/fontSize'
import { RepositoryProvider } from './ui/state/repositoryProvider'
import { TelemetryProvider } from './ui/state/telemetryProvider'
import { TelemetryConsentProvider } from './ui/state/telemetryConsentProvider'
import { AuthProvider } from './ui/auth/AuthProvider'
import { IdbProjectRepository } from './infrastructure/persistence/idb/IdbProjectRepository'
import { ConsentingTelemetryAdapter } from './infrastructure/telemetry/ConsentingTelemetryAdapter'
import { HttpTelemetryAdapter } from './infrastructure/telemetry/HttpTelemetryAdapter'
import { NoopTelemetryAdapter } from './infrastructure/telemetry/NoopTelemetryAdapter'
import { TelemetryConsentStore } from './application/telemetry/consentStore'
import { subscribeObservabilityToConsent } from './application/sentry/observabilitySubscriber'
import { BACKEND_ENABLED } from './infrastructure/config/featureFlags'

// Apply persisted font-size before first paint to avoid flash.
applyFontSize(readFontSize())

const repository = new IdbProjectRepository()
const consentStore = new TelemetryConsentStore(repository)
// Drive the Sentry SDK lifecycle off the same consent flag (ADR-035).
// The SDK is loaded lazily by initObservability so this call alone does
// not pull @sentry/react into the entry chunk.
subscribeObservabilityToConsent(consentStore)
// With BACKEND_ENABLED=false the HTTP transport is swapped for a noop
// so telemetry never reaches over the network even if consent gets
// flipped on locally. The consent gate stays in front so the runtime
// shape is unchanged when the flag flips back to true.
const telemetry = new ConsentingTelemetryAdapter(
  BACKEND_ENABLED ? new HttpTelemetryAdapter() : new NoopTelemetryAdapter(),
  () => consentStore.isConsenting(),
)

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <ThemeProvider>
      <RepositoryProvider repository={repository}>
        <TelemetryConsentProvider store={consentStore}>
          <TelemetryProvider telemetry={telemetry}>
            <AuthProvider>
              <RouterProvider router={router} />
            </AuthProvider>
          </TelemetryProvider>
        </TelemetryConsentProvider>
      </RepositoryProvider>
    </ThemeProvider>
  </StrictMode>,
)
